import { Box, Typography, Chip } from "@mui/material";
import { useDeviceStore } from '@/store/useDeviceStore';

interface TestInfoPanelProps {
  testInfo?: {
    createdAt: string;
    operatorName?: string;
    deviceUid?: string;
    status?: string;
    sampleCode?: string;
  };
}

/**
 * TestInfoPanel - Display test information in a 4-column layout
 * Shows Date, Operator, Device, and Status
 */
export function TestInfoPanel({ testInfo }: TestInfoPanelProps) {
  if (!testInfo) return null;

  const storeDevice = useDeviceStore(s => s.deviceInfo);
  const storeLabel = useDeviceStore(s => s.getDeviceLabel());

  const deviceLabel = (() => {
    if (storeDevice?.uid && storeDevice.uid === testInfo.deviceUid) {
      // connected device matches the test; show friendly label from store
      return storeLabel;
    }
    return testInfo.deviceUid ? `Reader ${testInfo.deviceUid}` : '';
  })();

  const getStatusChipColor = () => {
    const status = testInfo.status?.toUpperCase();
    switch (status) {
      case "COMPLETE":
      case "COMPLETED":
        return "#388e3c"; // green
      case "IN_PROGRESS":
      case "MEASURING":
      case "INCUBATING":
        return "#f57c00"; // orange
      case "FAILED":
      case "ERROR":
        return "#d32f2f"; // red
      default:
        return "#bdbdbd"; // gray
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        Test Information
      </Typography>

      {/* 4-Column Grid */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr 1fr" },
        gap: 2,
      }}>
        {/* Date Column */}
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            Test Date
          </Typography>
          <Typography variant="body2">
            {formatDate(testInfo.createdAt)}
          </Typography>
        </Box>

        {/* Operator Column */}
        {testInfo.operatorName && (
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
              Operator
            </Typography>
            <Typography variant="body2">
              {testInfo.operatorName}
            </Typography>
          </Box>
        )}

        {/* Device Column */}
        {testInfo.deviceUid && (
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
              Device
            </Typography>
            <Typography variant="body2">
              {deviceLabel}
            </Typography>
          </Box>
        )}

        {/* Status Column */}
        {testInfo.status && (
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
              Status
            </Typography>
            <Chip
              label={testInfo.status.toUpperCase()}
              sx={{
                backgroundColor: getStatusChipColor(),
                color: "#fff",
                fontWeight: 600,
                fontSize: "0.75rem",
                height: 24,
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}

